// Shared earnings module: the single source of truth for the contractor pay
// types. Both My Logs and contractor statement generation import this.
// Do NOT duplicate this math anywhere.
//
// Pay types live on the ASSIGNMENT (the user-client pairing):
//   hourly        raw hours for that client in the period x assignment.rate
//   flat_monthly  assignment.monthly_amount per calendar month, no proration
//   day_rate      count of DISTINCT entry_date with hours>0 x assignment.rate
//   fixed_project assignment.project_amount, once, in assignment.project_bill_month
//   hybrid        assignment.hybrid_base + max(0, memberHours - hybrid_included_hours)
//                 x hybrid_overage_rate. Base always applies, like flat_monthly.
//                 Its own numbers -- NOT the client's included hours.
//   percent       a single percentage (assignment.pay_percent) of the CLIENT's
//                 rate card, applied to the member's own hours. Needs the client
//                 row (model + rate/retainer/hours/overage cache) passed in as
//                 the 4th arg. Mirrors the client's billing structure at a
//                 discount: hourly -> % of $/hr; hybrid -> % of base + % of
//                 overage rate; retainer -> % of the retainer every month.
//
// IMPORTANT: contractor pay uses RAW (unrounded) hours. The billing rounding
// table applies only to client invoices, never here.
//
// Time left out of pay: an entry with is_payable === false is dropped before any
// of the math runs, so every pay type that counts hours (hourly, day_rate,
// hybrid overage, percent) ignores it, and flat_monthly / fixed_project (which
// never count hours) are unaffected. This is separate from is_billable, which is
// only about the client invoice. A missing is_payable counts as payable, so a
// query that forgets to select the column silently pays: every query that feeds
// pay must select it (see scripts/test-earnings-payable.mjs).

export function rawHours(entries) {
  return (entries || []).reduce((s, e) => s + parseFloat(e.hours || 0), 0);
}

export function distinctDays(entries) {
  return new Set(
    (entries || [])
      .filter(e => parseFloat(e.hours || 0) > 0)
      .map(e => e.entry_date)
  ).size;
}

// Compute one assignment's earnings for a client and period.
// assignment: { pay_type, rate, monthly_amount, project_amount, project_bill_month,
//               project_billed_at, pay_percent, hybrid_base, hybrid_included_hours,
//               hybrid_overage_rate }
// entries:    that user's entries for that client in the period (already filtered)
// period:     'YYYY-MM'
// client:     the client row, for pay_type='percent' only -- reads .model and the
//             billing cache (.rate / .retainer / .hours / .overage). Optional;
//             a missing client makes a percent line compute to 0.
// Returns: { payType, basis, quantity, rateOrAmount, lineTotal, note, appliesThisPeriod }
//   basis is one of 'hours' | 'days' | 'flat' | 'project' | 'hybrid' | 'percent'.
function earnFromEntries(assignment, entries, period, client) {
  const payType = assignment?.pay_type || 'hourly';

  if (payType === 'flat_monthly') {
    const amt = parseFloat(assignment.monthly_amount || 0);
    return { payType, basis: 'flat', quantity: 1, rateOrAmount: amt, lineTotal: amt, note: 'flat monthly', appliesThisPeriod: true };
  }

  if (payType === 'day_rate') {
    const days = distinctDays(entries);
    const rate = parseFloat(assignment.rate || 0);
    return { payType, basis: 'days', quantity: days, rateOrAmount: rate, lineTotal: days * rate, note: `${days} day${days === 1 ? '' : 's'}`, appliesThisPeriod: days > 0 };
  }

  if (payType === 'fixed_project') {
    const amt = parseFloat(assignment.project_amount || 0);
    // Appears once, only in its bill month, and only if not already billed on a
    // confirmed statement. Null bill month means pending: appears nowhere.
    const applies = !!assignment.project_bill_month
      && assignment.project_bill_month === period
      && !assignment.project_billed_at;
    const note = applies
      ? 'project'
      : (assignment.project_bill_month ? `bills ${assignment.project_bill_month}` : 'bill month not set');
    return { payType, basis: 'project', quantity: 1, rateOrAmount: amt, lineTotal: applies ? amt : 0, note, appliesThisPeriod: applies };
  }

  if (payType === 'hybrid') {
    const hrs = rawHours(entries);
    const base = parseFloat(assignment.hybrid_base || 0);
    const included = parseFloat(assignment.hybrid_included_hours || 0);
    const overRate = parseFloat(assignment.hybrid_overage_rate || 0);
    const over = Math.max(0, hrs - included);
    const total = base + over * overRate;
    const note = over > 0
      ? `base + ${over.toFixed(2)}h over ${included}`
      : `base (${hrs.toFixed(2)}h of ${included})`;
    // Base always applies, same as flat_monthly.
    return { payType, basis: 'hybrid', quantity: hrs, rateOrAmount: overRate, lineTotal: total, note, appliesThisPeriod: true };
  }

  if (payType === 'percent') {
    const pct = parseFloat(assignment.pay_percent || 0) / 100;
    const hrs = rawHours(entries);
    const model = client?.model || 'hourly';
    const pctLabel = `${parseFloat(assignment.pay_percent || 0)}%`;

    if (model === 'retainer') {
      const amt = parseFloat(client?.retainer || 0) * pct;
      return { payType, basis: 'percent', quantity: 1, rateOrAmount: amt, lineTotal: amt, note: `${pctLabel} of retainer`, appliesThisPeriod: true };
    }

    if (model === 'hybrid') {
      const included = parseFloat(client?.hours || 0);
      const over = Math.max(0, hrs - included);
      const memberBase = parseFloat(client?.retainer || 0) * pct;
      const memberOverRate = parseFloat(client?.overage || 0) * pct;
      const total = memberBase + over * memberOverRate;
      const note = over > 0
        ? `${pctLabel} of client billing, ${over.toFixed(2)}h over ${included}`
        : `${pctLabel} of client base (${hrs.toFixed(2)}h of ${included})`;
      // Base component always applies, mirroring the client's hybrid retainer.
      return { payType, basis: 'percent', quantity: hrs, rateOrAmount: memberOverRate, lineTotal: total, note, appliesThisPeriod: true };
    }

    // hourly (default / unknown model)
    const effRate = parseFloat(client?.rate || 0) * pct;
    return { payType, basis: 'percent', quantity: hrs, rateOrAmount: effRate, lineTotal: hrs * effRate, note: `${pctLabel} of $${parseFloat(client?.rate || 0)}/hr`, appliesThisPeriod: hrs > 0 };
  }

  // hourly (default)
  const hrs = rawHours(entries);
  const rate = parseFloat(assignment.rate || 0);
  return { payType, basis: 'hours', quantity: hrs, rateOrAmount: rate, lineTotal: hrs * rate, note: null, appliesThisPeriod: hrs > 0 };
}

export function isPayable(entry) {
  return entry?.is_payable !== false;
}

// The entry point everything uses. Same arguments and return shape as always,
// plus three fields about time left out of pay:
//   unpaidHours      hours on entries marked not paid (0 when none)
//   unpaidCount      how many entries
//   unpaidPayEffect  dollars of pay those hours would have added (0 for pay that
//                    doesn't count hours, e.g. flat monthly, so the UI can say
//                    "this doesn't change your pay" truthfully)
export function computeEarnings(assignment, entries, period, client) {
  const all = entries || [];
  const paid = all.filter(isPayable);
  const result = earnFromEntries(assignment, paid, period, client);
  if (paid.length === all.length) return { ...result, unpaidHours: 0, unpaidCount: 0, unpaidPayEffect: 0 };
  const unpaid = all.filter(e => !isPayable(e));
  const full = earnFromEntries(assignment, all, period, client);
  return {
    ...result,
    unpaidHours: rawHours(unpaid),
    unpaidCount: unpaid.length,
    unpaidPayEffect: Math.max(0, full.lineTotal - result.lineTotal),
  };
}

// Short human label for an assignment's pay structure, for tags/headers.
export function payStructureLabel(assignment) {
  const fmt = (n) => '$' + Math.round(parseFloat(n || 0)).toLocaleString();
  switch (assignment?.pay_type) {
    case 'flat_monthly':  return `${fmt(assignment.monthly_amount)}/mo`;
    case 'day_rate':      return `${fmt(assignment.rate)}/day`;
    case 'fixed_project': return `${fmt(assignment.project_amount)} project`;
    case 'hybrid':        return `${fmt(assignment.hybrid_base)}/mo + ${fmt(assignment.hybrid_overage_rate)}/hr over ${parseFloat(assignment.hybrid_included_hours || 0)}h`;
    case 'percent':       return `${parseFloat(assignment.pay_percent || 0)}% of client rate`;
    default:              return `${fmt(assignment.rate)}/hr`;
  }
}
